import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { MapPin, Crosshair, X, CalendarOff } from "lucide-react";

function Field({ label, children }) {
    return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

function NoSchoolDates({ dates, onChange }) {
    const [d, setD] = React.useState("");
    function add() {
        if (!d) return;
        if (new Date(`${d}T12:00:00`).getDay() !== 0) { toast.error("Pick a Sunday"); return; }
        onChange([...new Set([...dates, d])].sort());
        setD("");
    }
    return (
        <div>
            <Label className="text-xs flex items-center gap-1"><CalendarOff className="w-3.5 h-3.5" /> No-school Sundays</Label>
            <div className="flex gap-2 mt-1">
                <Input type="date" value={d} onChange={e => setD(e.target.value)} className="w-44" data-testid="no-school-date-input" />
                <Button type="button" variant="outline" onClick={add} data-testid="no-school-date-add">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
                {dates.length === 0 && <span className="text-xs text-brand-stone">None — every Sunday is a school day.</span>}
                {dates.map(x => (
                    <Badge key={x} variant="secondary" className="gap-1" data-testid={`no-school-${x}`}>
                        {x}
                        <button onClick={() => onChange(dates.filter(y => y !== x))} data-testid={`no-school-remove-${x}`}><X className="w-3 h-3" /></button>
                    </Badge>
                ))}
            </div>
        </div>
    );
}

/** Admin: school location, check-in radius/window and no-school Sundays. */
export function SchoolCheckinSettings() {
    const [s, setS] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    React.useEffect(() => { api.get("/school-settings").then(r => setS(r.data)); }, []);
    if (!s) return null;
    const set = (k, num) => (e) => setS(p => ({ ...p, [k]: num ? Number(e.target.value) : e.target.value }));

    function useHere() {
        navigator.geolocation?.getCurrentPosition(
            (pos) => { setS(p => ({ ...p, lat: +pos.coords.latitude.toFixed(7), lng: +pos.coords.longitude.toFixed(7) })); toast.success("Location filled from this device — remember to save"); },
            () => toast.error("Couldn't get this device's location"),
            { enableHighAccuracy: true, timeout: 15000 });
    }
    async function save() {
        setBusy(true);
        try {
            const { address, lat, lng, radius_m, school_start, checkin_start, checkin_end, no_school_dates } = s;
            const { data } = await api.put("/admin/school-settings", { address, lat, lng, radius_m, school_start: school_start || "10:00", checkin_start, checkin_end, no_school_dates });
            setS(data);
            toast.success("Check-in settings saved");
        } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    }
    return (
        <Card className="mb-6" data-testid="school-checkin-settings">
            <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 font-dash font-semibold"><MapPin className="w-4 h-4 text-primary" /> School start time & location check-in</div>
                <p className="text-xs text-brand-stone -mt-2">Anyone who checks in (by location, "I'm here", or an admin-entered arrival time) after the school start time is marked late with the exact minutes. Location check-in works within range during the window on school Sundays (Pacific time).</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="sm:col-span-2"><Field label="School address"><Input value={s.address || ""} onChange={set("address")} data-testid="school-address" /></Field></div>
                    <Field label="Latitude"><Input type="number" step="any" value={s.lat} onChange={set("lat", true)} data-testid="school-lat" /></Field>
                    <Field label="Longitude"><Input type="number" step="any" value={s.lng} onChange={set("lng", true)} data-testid="school-lng" /></Field>
                    <Field label="Range (meters)"><Input type="number" min={50} max={1000} value={s.radius_m} onChange={set("radius_m", true)} data-testid="school-radius" /></Field>
                    <Field label="School starts (late after)"><Input type="time" value={s.school_start || "10:00"} onChange={set("school_start")} data-testid="school-start-time" /></Field>
                    <Field label="Check-in opens"><Input type="time" value={s.checkin_start} onChange={set("checkin_start")} data-testid="school-checkin-start" /></Field>
                    <Field label="Check-in closes"><Input type="time" value={s.checkin_end} onChange={set("checkin_end")} data-testid="school-checkin-end" /></Field>
                    <div className="flex items-end"><Button type="button" variant="outline" onClick={useHere} className="gap-1.5 w-full" data-testid="school-use-here"><Crosshair className="w-4 h-4" /> Use my location</Button></div>
                </div>
                <NoSchoolDates dates={s.no_school_dates || []} onChange={v => setS(p => ({ ...p, no_school_dates: v }))} />
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <a className="text-xs text-primary underline" href={`https://maps.google.com/?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" data-testid="school-map-link">Check pin on map</a>
                    <Button onClick={save} disabled={busy} data-testid="school-settings-save">{busy ? "Saving…" : "Save settings"}</Button>
                </div>
            </CardContent>
        </Card>
    );
}
